import { ColorRgba } from "types";
import { RgbaColor } from "react-colorful";
import { RoleType } from "store/auth/auth.type";

export const isPrimitive = (value: unknown): value is string | number | boolean => /^(b|st|n)/.test(typeof value);
export type Primitive = string | number | boolean;
export const isNil = (value: unknown): value is undefined | null => typeof value === "undefined" || value === null;

export const isEmpty = (value: unknown): boolean => {
  if (isNil(value)) {
    return true;
  }
  if (typeof value === "object" && value !== null) {
    return Object.keys(value).length === 0;
  }
  if (typeof value === "string" || Array.isArray(value)) {
    return value.length === 0;
  }
  return false;
};

/**
 * @see https://malcolmkee.com/blog/typesafe-call-all
 * */
type CallBack<Params extends readonly unknown[]> = (...args: Params) => void;

export const callAll =
  <Params extends readonly unknown[]>(...fns: Array<CallBack<Params> | undefined>) =>
  (...args: Params) =>
    fns.forEach((fn) => typeof fn === "function" && fn(...args));

export type ToolTipStyleParams = {
  top: number;
  left: number;
  height: number;
  width: number;
  disabledPointerEvent?: boolean;
};

export const toolTipStyle = ({
  top,
  height,
  width,
  left,
  disabledPointerEvent = true,
}: ToolTipStyleParams): React.CSSProperties => {
  const newStyles = { zIndex: 1, pointerEvents: disabledPointerEvent && "none", left } as React.CSSProperties;
  const windowHeight = window.innerHeight;
  const windowWidth = window.innerWidth;
  if (windowHeight - top > height) {
    newStyles.top = top - 10;
  } else if (top > height) {
    newStyles.bottom = windowHeight - top;
  } else if (windowHeight - height < 0) {
    newStyles.top = 85;
  } else {
    newStyles.top = windowHeight - height;
  }

  if (windowWidth - left > width + 80) {
    newStyles.left = left;
  } else {
    newStyles.left = left - width;
  }
  return newStyles;
};

export const getParameterByIndex = (index: number): string => {
  const pathname = window.location.pathname.replace("/", "");
  const arrPath = pathname.split("/");
  if (arrPath.length - 1 < index) {
    return "";
  }
  return "/" + arrPath[index];
};

export function isJson(str: string) {
  try {
    JSON.parse(str);
  } catch {
    return false;
  }
  return true;
}

export const isDefined = <T>(value: T | undefined): value is T => typeof value !== "undefined";

export function classNames(...classes: (false | null | undefined | string)[]): string {
  return classes.filter(Boolean).join(" ");
}

export const levenshteinDistance = (str1 = "", str2 = "") => {
  const track = Array(str2.length + 1)
    .fill(null)
    .map(() => Array(str1.length + 1).fill(null));
  for (let i = 0; i <= str1.length; i += 1) {
    track[0][i] = i;
  }
  for (let j = 0; j <= str2.length; j += 1) {
    track[j][0] = j;
  }
  for (let j = 1; j <= str2.length; j += 1) {
    for (let i = 1; i <= str1.length; i += 1) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      track[j][i] = Math.min(
        track[j][i - 1] + 1, // deletion
        track[j - 1][i] + 1, // insertion
        track[j - 1][i - 1] + indicator, // substitution
      );
    }
  }
  return track[str2.length][str1.length];
};

export function parseQueryString(queryString: string) {
  const query: { [key: string]: string | string[] | undefined } = {};
  const pairs = (queryString[0] === "?" ? queryString.substr(1) : queryString).split("&");
  for (let i = 0; i < pairs.length; i++) {
    const pair = pairs[i].split("=");
    query[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || "");
  }
  return query;
}

type Noop = () => void;
export const noop: Noop = () => {
  /**/
};

export function stringifyQueryString(input: { [key: string]: string | string[] | undefined }) {
  return Object.entries(input)
    .reduce((previousValue: string[], [k, v]) => {
      if (isEmpty(v)) {
        return previousValue;
      }
      return [...previousValue, `${k}=${v}`];
    }, [])
    .join("&");
}

export function checkPermission(routeRoles: RoleType[], userRoles: RoleType[]) {
  return routeRoles.some((item) => userRoles.includes(item));
}

export const formatRGBAColorToString = (color: RgbaColor) =>
  `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a ?? 1})`;

/**
 * Type guard to check if an array is a valid ColorRgba
 * ColorRgba must have at least 3 elements (RGB) and at most 4 elements (RGBA)
 */
export const isValidColorRgba = (color: number[]): color is ColorRgba => {
  return Array.isArray(color) && color.length >= 3 && color.length <= 4 && color.every((val) => val >= 0 && val <= 255);
};

/**
 * Convert an array to ColorRgba with validation and fallback
 * @param color - Array to convert
 * @param defaultColor - Default value if color is invalid
 * @returns Valid ColorRgba or fallback
 */
export const toColorRgba = (color: number[], defaultColor: ColorRgba): ColorRgba => {
  if (isValidColorRgba(color)) {
    // Ensure it's a proper tuple by extracting first 3-4 elements
    if (color.length === 3) {
      return [color[0], color[1], color[2]];
    }
    return [color[0], color[1], color[2], color[3]];
  }
  return defaultColor;
};

/**
 * Convert GraphQL color array (can be null/undefined) to ColorRgba
 * @param color - GraphQL color array from API
 * @param defaultColor - Default value
 * @returns Valid ColorRgba or defaultColor
 */
export const fromGraphQLColor = (color: number[] | null | undefined, defaultColor: ColorRgba): ColorRgba => {
  if (!color) {
    return defaultColor;
  }
  return toColorRgba(color, defaultColor);
};

/**
 * Converts a ColorRgba tuple to RGBA string format.
 * @param color - A tuple of 3 or 4 numbers representing RGB or RGBA color values.
 *   - If 3 elements: [R, G, B] where values are 0-255
 *   - If 4 elements: [R, G, B, A] where R, G, B are 0-255 and A is 0-255 (will be normalized to 0-1)
 * @returns A string in the format "rgba(R, G, B, A)" where alpha is normalized to 0-1 range
 */
export const formatPolicyColorToString = (color: ColorRgba) => {
  const [r, g, b, a] = color;
  const alpha = a !== undefined ? a / 255 : 1;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const formatArrayToRgbaColor = (colors?: ColorRgba) => {
  if (!colors) return { r: 0, g: 0, b: 0, a: 1 };
  return {
    r: colors[0],
    g: colors[1],
    b: colors[2],
    a: colors[3] !== undefined ? Number((colors[3] / 255).toFixed(2)) : 1,
  };
};

export const convertHexToRgba = (hexCode: string) => {
  let hex = hexCode.replace("#", "");
  let opacity = 1;

  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  if (hex.length === 8) {
    opacity = parseInt(hex.substring(6, 8), 16);
  }

  opacity = Number((opacity / 255).toFixed(2));

  return {
    r,
    g,
    b,
    a: opacity,
  };
};

export const convertRgbaToHex = (rgba: RgbaColor) => {
  let r = (+rgba.r).toString(16);
  let g = (+rgba.g).toString(16);
  let b = (+rgba.b).toString(16);
  let a = (+(rgba.a * 255)).toString(16);

  if (r.length === 1) r = "0" + r;
  if (g.length === 1) g = "0" + g;
  if (b.length === 1) b = "0" + b;
  if (a.length === 1) a = "0" + a;

  return "#" + r + g + b + a;
};

export const getQueryParams = <
  Params extends { [key: string]: string | string[] | undefined } = {
    [key: string]: string | undefined;
  },
>() => {
  const { search } = window.location;
  const params = parseQueryString(search);
  return params as Params;
};
export const DOTS = "...";
export const generatePaginationRange = (currentPage: number, totalPages: number) => {
  const numberOfPages = 2; // Number of pages to show on each side of current page
  const range: (number | string)[] = [];

  for (let i = 1; i <= totalPages; i++) {
    if (
      i === 1 || // Always show first page
      i === totalPages || // Always show last page
      (i >= currentPage - numberOfPages && i <= currentPage + numberOfPages) // Show pages around current page
    ) {
      range.push(i);
    } else if (range[range.length - 1] !== DOTS) {
      range.push(DOTS);
    }
  }

  return range;
};

export const convertColor = (color?: number[]) => {
  return `rgba(${color?.join(",")})`;
};
